import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    getZones,
    getZoneArborescence,
    getZoneDetail,
    getZoneStats,
    createZone,
    updateZone,
    deleteZone,
    assignZoneResponsable,
    getCommerciaux,
    getUsers
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
    FiMap, FiPlus, FiEdit2, FiTrash2, FiSearch, FiChevronRight, FiChevronDown,
    FiUser, FiUsers, FiMapPin, FiBarChart2, FiX, FiUserPlus, FiGitBranch,
    FiList, FiGrid, FiRefreshCw
} from 'react-icons/fi';

// --- Types ---

interface Zone {
    id: number;
    nom: string;
    code: string;
    description?: string;
    type: string;
    parent_id: number | null;
    parent_nom?: string;
    responsable_id: number | null;
    responsable_nom?: string;
    responsable_prenom?: string;
    responsable_photo?: string;
    latitude_centre?: number;
    longitude_centre?: number;
    rayon_km?: number;
    couleur?: string;
    nb_commerciaux?: number;
    nb_clients?: number;
    children?: Zone[];
    actif?: boolean;
}

interface ZoneForm {
    nom: string;
    code: string;
    description: string;
    type: string;
    parent_id: string;
    responsable_id: string;
    latitude_centre: string;
    longitude_centre: string;
    rayon_km: string;
    couleur: string;
}

interface ZoneStatsData {
    nb_commerciaux: number;
    nb_clients: number;
    nb_activites: number;
    chiffre_affaires: number;
}

// --- Constants ---

const ZONE_TYPES = [
    { value: 'region', label: 'Region', color: '#16A34A' },
    { value: 'departement', label: 'Departement', color: '#3B82F6' },
    { value: 'commune', label: 'Commune', color: '#F59E0B' },
    { value: 'quartier', label: 'Quartier', color: '#29B6E7' },
    { value: 'secteur', label: 'Secteur', color: '#8B5CF6' },
    { value: 'point', label: 'Point', color: '#EF4444' }
];

const TYPE_HIERARCHY: Record<string, string> = {
    region: 'departement',
    departement: 'commune',
    commune: 'quartier',
    quartier: 'secteur',
    secteur: 'point'
};

const TYPE_COLOR_MAP: Record<string, string> = {
    region: '#16A34A',
    departement: '#3B82F6',
    commune: '#F59E0B',
    quartier: '#29B6E7',
    secteur: '#8B5CF6',
    point: '#EF4444'
};

const EMPTY_FORM: ZoneForm = {
    nom: '',
    code: '',
    description: '',
    type: 'region',
    parent_id: '',
    responsable_id: '',
    latitude_centre: '',
    longitude_centre: '',
    rayon_km: '',
    couleur: '#3B82F6'
};

// --- Helpers ---

function getTypeBadgeColor(type: string): string {
    return TYPE_COLOR_MAP[type] || '#6B7280';
}

function flattenTree(nodes: Zone[]): Zone[] {
    const result: Zone[] = [];
    function walk(list: Zone[]) {
        for (const z of list) {
            result.push(z);
            if (z.children?.length) walk(z.children);
        }
    }
    walk(nodes);
    return result;
}

function buildHierarchyLabel(zone: Zone, allZones: Zone[], depth = 0): string {
    const prefix = '\u00A0\u00A0'.repeat(depth);
    return `${prefix}${zone.nom} (${zone.type})`;
}

function buildFlatOptionsFromTree(nodes: Zone[], depth = 0): { id: number; label: string; type: string }[] {
    const options: { id: number; label: string; type: string }[] = [];
    for (const node of nodes) {
        const prefix = '\u2014'.repeat(depth);
        options.push({ id: node.id, label: `${prefix ? prefix + ' ' : ''}${node.nom}`, type: node.type });
        if (node.children?.length) {
            options.push(...buildFlatOptionsFromTree(node.children, depth + 1));
        }
    }
    return options;
}

// --- Sub-components ---

const TypeBadge: React.FC<{ type: string }> = ({ type }) => (
    <span
        className="badge"
        style={{
            backgroundColor: getTypeBadgeColor(type) + '18',
            color: getTypeBadgeColor(type),
            border: `1px solid ${getTypeBadgeColor(type)}40`,
            fontSize: '0.75rem',
            padding: '2px 8px',
            borderRadius: '12px'
        }}
    >
        {type}
    </span>
);

interface TreeNodeProps {
    zone: Zone;
    depth: number;
    expandedIds: Set<number>;
    selectedZoneId: number | null;
    searchTerm: string;
    onToggle: (id: number) => void;
    onSelect: (zone: Zone) => void;
    onEdit: (zone: Zone) => void;
    onAddChild: (zone: Zone) => void;
    onAssign: (zone: Zone) => void;
    onDelete: (zone: Zone) => void;
}

const TreeNode: React.FC<TreeNodeProps> = ({
    zone, depth, expandedIds, selectedZoneId, searchTerm,
    onToggle, onSelect, onEdit, onAddChild, onAssign, onDelete
}) => {
    const hasChildren = zone.children && zone.children.length > 0;
    const isExpanded = expandedIds.has(zone.id);
    const isSelected = selectedZoneId === zone.id;

    const matchesSearch = searchTerm
        ? zone.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
          zone.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          zone.type.toLowerCase().includes(searchTerm.toLowerCase())
        : true;

    const childrenMatchSearch = searchTerm && zone.children
        ? zone.children.some(c => treeMatchesSearch(c, searchTerm))
        : false;

    if (searchTerm && !matchesSearch && !childrenMatchSearch) {
        return null;
    }

    return (
        <div className="tree-node-wrapper">
            <div
                className={`tree-node ${isSelected ? 'tree-node-selected' : ''}`}
                style={{ paddingLeft: `${depth * 24 + 8}px` }}
                onClick={() => onSelect(zone)}
            >
                {depth > 0 && (
                    <div
                        className="tree-line"
                        style={{
                            left: `${(depth - 1) * 24 + 20}px`,
                            borderLeft: `2px solid ${getTypeBadgeColor(zone.type)}30`
                        }}
                    />
                )}
                <button
                    className="tree-toggle"
                    onClick={(e) => { e.stopPropagation(); if (hasChildren) onToggle(zone.id); }}
                    style={{ visibility: hasChildren ? 'visible' : 'hidden' }}
                >
                    {isExpanded ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                </button>

                <div className="tree-node-content">
                    <div className="tree-node-main">
                        <span className="tree-node-name">{zone.nom}</span>
                        <TypeBadge type={zone.type} />
                    </div>
                    <div className="tree-node-meta">
                        {zone.responsable_nom && (
                            <span className="tree-meta-item">
                                <FiUser size={12} />
                                {zone.responsable_prenom} {zone.responsable_nom}
                            </span>
                        )}
                        <span className="tree-meta-item">
                            <FiUsers size={12} /> {zone.nb_commerciaux || 0}
                        </span>
                        <span className="tree-meta-item">
                            <FiMapPin size={12} /> {zone.nb_clients || 0}
                        </span>
                    </div>
                </div>

                <div className="tree-node-actions">
                    <button className="btn-icon" onClick={(e) => { e.stopPropagation(); onEdit(zone); }} title="Modifier">
                        <FiEdit2 size={14} />
                    </button>
                    {zone.type !== 'point' && (
                        <button className="btn-icon" onClick={(e) => { e.stopPropagation(); onAddChild(zone); }} title="Ajouter sous-zone">
                            <FiPlus size={14} />
                        </button>
                    )}
                    <button className="btn-icon" onClick={(e) => { e.stopPropagation(); onAssign(zone); }} title="Assigner responsable">
                        <FiUserPlus size={14} />
                    </button>
                    <button className="btn-icon btn-icon-danger" onClick={(e) => { e.stopPropagation(); onDelete(zone); }} title="Supprimer">
                        <FiTrash2 size={14} />
                    </button>
                </div>
            </div>

            {hasChildren && isExpanded && (
                <div className="tree-children">
                    {zone.children!.map(child => (
                        <TreeNode
                            key={child.id}
                            zone={child}
                            depth={depth + 1}
                            expandedIds={expandedIds}
                            selectedZoneId={selectedZoneId}
                            searchTerm={searchTerm}
                            onToggle={onToggle}
                            onSelect={onSelect}
                            onEdit={onEdit}
                            onAddChild={onAddChild}
                            onAssign={onAssign}
                            onDelete={onDelete}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

function treeMatchesSearch(zone: Zone, term: string): boolean {
    const matches = zone.nom.toLowerCase().includes(term.toLowerCase()) ||
        zone.code?.toLowerCase().includes(term.toLowerCase()) ||
        zone.type.toLowerCase().includes(term.toLowerCase());
    if (matches) return true;
    if (zone.children) return zone.children.some(c => treeMatchesSearch(c, term));
    return false;
}

// --- Main Component ---

const ZoneManagement: React.FC = () => {
    const { user, isAdmin } = useAuth();

    // Data states
    const [arborescence, setArborescence] = useState<Zone[]>([]);
    const [flatZones, setFlatZones] = useState<Zone[]>([]);
    const [users, setUsers] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    // UI states
    const [tab, setTab] = useState<'arborescence' | 'liste'>('arborescence');
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [parentFilter, setParentFilter] = useState('');
    const [sortField, setSortField] = useState<string>('nom');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
    const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

    // Selection / detail
    const [selectedZone, setSelectedZone] = useState<Zone | null>(null);
    const [zoneStats, setZoneStats] = useState<ZoneStatsData | null>(null);
    const [zoneDetail, setZoneDetail] = useState<any>(null);

    // Modals
    const [showFormModal, setShowFormModal] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [editZone, setEditZone] = useState<Zone | null>(null);
    const [assignTarget, setAssignTarget] = useState<Zone | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<Zone | null>(null);
    const [form, setForm] = useState<ZoneForm>({ ...EMPTY_FORM });
    const [assignResponsableId, setAssignResponsableId] = useState('');
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [submitting, setSubmitting] = useState(false);

    // --- Data Loading ---

    const loadArborescence = useCallback(async () => {
        try {
            const res = await getZoneArborescence();
            const data = Array.isArray(res.data) ? res.data : (res.data.data || []);
            setArborescence(data);
        } catch {
            setArborescence([]);
        }
    }, []);

    const loadFlatZones = useCallback(async () => {
        try {
            const res = await getZones();
            const data = Array.isArray(res.data) ? res.data : (res.data.data || []);
            setFlatZones(data);
        } catch {
            setFlatZones([]);
        }
    }, []);

    const loadUsers = useCallback(async () => {
        try {
            const res = await getUsers({ limit: 500 });
            const data = Array.isArray(res.data) ? res.data : (res.data.data || []);
            setUsers(data);
        } catch {
            setUsers([]);
        }
    }, []);

    const loadAll = useCallback(async () => {
        setLoading(true);
        await Promise.all([loadArborescence(), loadFlatZones(), loadUsers()]);
        setLoading(false);
    }, [loadArborescence, loadFlatZones, loadUsers]);

    useEffect(() => { loadAll(); }, [loadAll]);

    // --- Derived Data ---

    const parentOptions = useMemo(() => {
        return buildFlatOptionsFromTree(arborescence);
    }, [arborescence]);

    const managersAndAdmins = useMemo(() => {
        return users.filter(u =>
            ['admin', 'superadmin', 'manager', 'supervisor'].includes(u.role)
        );
    }, [users]);

    const filteredFlatZones = useMemo(() => {
        let list = [...flatZones];
        if (search) {
            const term = search.toLowerCase();
            list = list.filter(z =>
                z.nom.toLowerCase().includes(term) ||
                z.code?.toLowerCase().includes(term) ||
                z.type?.toLowerCase().includes(term)
            );
        }
        if (typeFilter) {
            list = list.filter(z => z.type === typeFilter);
        }
        if (parentFilter) {
            list = list.filter(z => String(z.parent_id) === parentFilter);
        }
        list.sort((a: any, b: any) => {
            const aVal = a[sortField] || '';
            const bVal = b[sortField] || '';
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
            }
            const cmp = String(aVal).localeCompare(String(bVal), 'fr');
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return list;
    }, [flatZones, search, typeFilter, parentFilter, sortField, sortDir]);

    // --- Zone selection & detail ---

    const handleSelectZone = useCallback(async (zone: Zone) => {
        setSelectedZone(zone);
        setZoneStats(null);
        setZoneDetail(null);
        try {
            const [statsRes, detailRes] = await Promise.all([
                getZoneStats(zone.id),
                getZoneDetail(zone.id)
            ]);
            setZoneStats(statsRes.data);
            setZoneDetail(detailRes.data);
        } catch {
            // Stats/detail may not be available
        }
    }, []);

    // --- Tree expand/collapse ---

    const toggleExpand = useCallback((id: number) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const expandAll = useCallback(() => {
        const allIds = new Set<number>();
        function walk(nodes: Zone[]) {
            for (const n of nodes) {
                if (n.children?.length) {
                    allIds.add(n.id);
                    walk(n.children);
                }
            }
        }
        walk(arborescence);
        setExpandedIds(allIds);
    }, [arborescence]);

    const collapseAll = useCallback(() => {
        setExpandedIds(new Set());
    }, []);

    // --- Form handlers ---

    const openCreateForm = useCallback(() => {
        setEditZone(null);
        setForm({ ...EMPTY_FORM });
        setFormErrors({});
        setShowFormModal(true);
    }, []);

    const openCreateChild = useCallback((parent: Zone) => {
        setEditZone(null);
        const childType = TYPE_HIERARCHY[parent.type] || 'secteur';
        setForm({
            ...EMPTY_FORM,
            parent_id: String(parent.id),
            type: childType
        });
        setFormErrors({});
        setShowFormModal(true);
    }, []);

    const openEditForm = useCallback((zone: Zone) => {
        setEditZone(zone);
        setForm({
            nom: zone.nom || '',
            code: zone.code || '',
            description: zone.description || '',
            type: zone.type || 'region',
            parent_id: zone.parent_id ? String(zone.parent_id) : '',
            responsable_id: zone.responsable_id ? String(zone.responsable_id) : '',
            latitude_centre: zone.latitude_centre != null ? String(zone.latitude_centre) : '',
            longitude_centre: zone.longitude_centre != null ? String(zone.longitude_centre) : '',
            rayon_km: zone.rayon_km != null ? String(zone.rayon_km) : '',
            couleur: zone.couleur || '#3B82F6'
        });
        setFormErrors({});
        setShowFormModal(true);
    }, []);

    const handleParentChange = useCallback((parentId: string) => {
        setForm(prev => {
            const next = { ...prev, parent_id: parentId };
            if (parentId) {
                const parentZone = parentOptions.find(p => String(p.id) === parentId);
                if (parentZone) {
                    const childType = TYPE_HIERARCHY[parentZone.type];
                    if (childType) next.type = childType;
                }
            }
            return next;
        });
    }, [parentOptions]);

    const validateForm = useCallback((): boolean => {
        const errors: Record<string, string> = {};
        if (!form.nom.trim()) errors.nom = 'Le nom est requis';
        if (!form.code.trim()) errors.code = 'Le code est requis';
        if (!form.type) errors.type = 'Le type est requis';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    }, [form]);

    const handleFormSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;
        setSubmitting(true);
        try {
            const payload: any = {
                nom: form.nom.trim(),
                code: form.code.trim(),
                description: form.description.trim() || null,
                type: form.type,
                parent_id: form.parent_id ? Number(form.parent_id) : null,
                responsable_id: form.responsable_id ? Number(form.responsable_id) : null,
                latitude_centre: form.latitude_centre ? parseFloat(form.latitude_centre) : null,
                longitude_centre: form.longitude_centre ? parseFloat(form.longitude_centre) : null,
                rayon_km: form.rayon_km ? parseFloat(form.rayon_km) : null,
                couleur: form.couleur || null
            };
            if (editZone) {
                await updateZone(editZone.id, payload);
            } else {
                await createZone(payload);
            }
            setShowFormModal(false);
            await loadAll();
        } catch (err: any) {
            const msg = err.response?.data?.error || err.response?.data?.message || 'Erreur lors de la sauvegarde';
            alert(msg);
        } finally {
            setSubmitting(false);
        }
    }, [form, editZone, validateForm, loadAll]);

    // --- Assign responsable ---

    const openAssignModal = useCallback((zone: Zone) => {
        setAssignTarget(zone);
        setAssignResponsableId(zone.responsable_id ? String(zone.responsable_id) : '');
        setShowAssignModal(true);
    }, []);

    const handleAssignSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        if (!assignTarget) return;
        setSubmitting(true);
        try {
            await assignZoneResponsable(assignTarget.id, {
                responsable_id: assignResponsableId ? Number(assignResponsableId) : null
            });
            setShowAssignModal(false);
            await loadAll();
        } catch (err: any) {
            alert(err.response?.data?.error || 'Erreur');
        } finally {
            setSubmitting(false);
        }
    }, [assignTarget, assignResponsableId, loadAll]);

    // --- Delete ---

    const openDeleteConfirm = useCallback((zone: Zone) => {
        setDeleteTarget(zone);
        setShowDeleteConfirm(true);
    }, []);

    const handleDelete = useCallback(async () => {
        if (!deleteTarget) return;
        setSubmitting(true);
        try {
            await deleteZone(deleteTarget.id);
            setShowDeleteConfirm(false);
            setDeleteTarget(null);
            if (selectedZone?.id === deleteTarget.id) {
                setSelectedZone(null);
                setZoneStats(null);
                setZoneDetail(null);
            }
            await loadAll();
        } catch (err: any) {
            alert(err.response?.data?.error || 'Impossible de supprimer cette zone');
        } finally {
            setSubmitting(false);
        }
    }, [deleteTarget, selectedZone, loadAll]);

    // --- Sort handler for table ---

    const handleSort = useCallback((field: string) => {
        setSortDir(prev => sortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'asc');
        setSortField(field);
    }, [sortField]);

    const sortIcon = (field: string) => {
        if (sortField !== field) return '';
        return sortDir === 'asc' ? ' \u25B2' : ' \u25BC';
    };

    // --- Render ---

    if (loading) {
        return (
            <div className="page-zones">
                <div className="page-header">
                    <h2><FiMap /> Gestion des Zones</h2>
                </div>
                <div style={{ textAlign: 'center', padding: '60px 0', color: '#6B7280' }}>
                    Chargement...
                </div>
            </div>
        );
    }

    return (
        <div className="page-zones">
            {/* Header */}
            <div className="page-header">
                <h2><FiMap /> Gestion des Zones</h2>
                <div className="header-actions">
                    <button className="btn btn-secondary" onClick={loadAll} title="Rafraichir">
                        <FiRefreshCw /> Rafraichir
                    </button>
                    <button className="btn btn-primary" onClick={openCreateForm}>
                        <FiPlus /> Nouvelle zone
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className="tabs">
                <button
                    className={`tab ${tab === 'arborescence' ? 'active' : ''}`}
                    onClick={() => setTab('arborescence')}
                >
                    <FiGitBranch /> Arborescence
                </button>
                <button
                    className={`tab ${tab === 'liste' ? 'active' : ''}`}
                    onClick={() => setTab('liste')}
                >
                    <FiList /> Liste
                </button>
            </div>

            {/* Arborescence Tab */}
            {tab === 'arborescence' && (
                <div className="zone-arborescence-layout">
                    <div className="zone-tree-panel">
                        <div className="filters-bar">
                            <div className="search-box">
                                <FiSearch />
                                <input
                                    placeholder="Rechercher une zone..."
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                />
                            </div>
                            <div className="tree-controls">
                                <button className="btn btn-secondary btn-sm" onClick={expandAll}>
                                    Tout ouvrir
                                </button>
                                <button className="btn btn-secondary btn-sm" onClick={collapseAll}>
                                    Tout fermer
                                </button>
                            </div>
                        </div>

                        <div className="tree-container">
                            {arborescence.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                                    Aucune zone trouvee. Cliquez sur "+ Nouvelle zone" pour commencer.
                                </div>
                            ) : (
                                arborescence.map(zone => (
                                    <TreeNode
                                        key={zone.id}
                                        zone={zone}
                                        depth={0}
                                        expandedIds={expandedIds}
                                        selectedZoneId={selectedZone?.id || null}
                                        searchTerm={search}
                                        onToggle={toggleExpand}
                                        onSelect={handleSelectZone}
                                        onEdit={openEditForm}
                                        onAddChild={openCreateChild}
                                        onAssign={openAssignModal}
                                        onDelete={openDeleteConfirm}
                                    />
                                ))
                            )}
                        </div>
                    </div>

                    {/* Detail Panel */}
                    {selectedZone && (
                        <div className="zone-detail-panel">
                            <div className="zone-detail-header">
                                <div>
                                    <h3>{selectedZone.nom}</h3>
                                    <TypeBadge type={selectedZone.type} />
                                    {selectedZone.code && (
                                        <span className="text-muted" style={{ marginLeft: 8, fontSize: '0.85rem' }}>
                                            {selectedZone.code}
                                        </span>
                                    )}
                                </div>
                                <button className="btn-icon" onClick={() => { setSelectedZone(null); setZoneStats(null); setZoneDetail(null); }}>
                                    <FiX />
                                </button>
                            </div>

                            {selectedZone.description && (
                                <p className="text-muted" style={{ margin: '8px 0 16px' }}>
                                    {selectedZone.description}
                                </p>
                            )}

                            {/* Stats KPI */}
                            <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '16px' }}>
                                <div className="kpi-card">
                                    <div className="kpi-value">{zoneStats?.nb_commerciaux ?? selectedZone.nb_commerciaux ?? 0}</div>
                                    <div className="kpi-label">Commerciaux</div>
                                </div>
                                <div className="kpi-card">
                                    <div className="kpi-value">{zoneStats?.nb_clients ?? selectedZone.nb_clients ?? 0}</div>
                                    <div className="kpi-label">Clients</div>
                                </div>
                                <div className="kpi-card">
                                    <div className="kpi-value">{zoneStats?.nb_activites ?? 0}</div>
                                    <div className="kpi-label">Activites</div>
                                </div>
                                <div className="kpi-card">
                                    <div className="kpi-value">
                                        {zoneStats?.chiffre_affaires
                                            ? Number(zoneStats.chiffre_affaires).toLocaleString('fr') + ' F'
                                            : '0 F'}
                                    </div>
                                    <div className="kpi-label">CA</div>
                                </div>
                            </div>

                            {/* Responsable */}
                            <div className="chart-card" style={{ marginBottom: '16px' }}>
                                <h4 style={{ margin: '0 0 8px', fontSize: '0.9rem' }}><FiUser /> Responsable</h4>
                                {selectedZone.responsable_nom ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <div style={{
                                            width: 36, height: 36, borderRadius: '50%',
                                            backgroundColor: '#E5E7EB', display: 'flex',
                                            alignItems: 'center', justifyContent: 'center',
                                            fontWeight: 600, fontSize: '0.85rem', color: '#374151',
                                            overflow: 'hidden'
                                        }}>
                                            {selectedZone.responsable_photo ? (
                                                <img
                                                    src={selectedZone.responsable_photo}
                                                    alt=""
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                />
                                            ) : (
                                                `${(selectedZone.responsable_prenom || '')[0] || ''}${(selectedZone.responsable_nom || '')[0] || ''}`
                                            )}
                                        </div>
                                        <span>{selectedZone.responsable_prenom} {selectedZone.responsable_nom}</span>
                                    </div>
                                ) : (
                                    <span className="text-muted">Aucun responsable assigne</span>
                                )}
                            </div>

                            {/* Children zones */}
                            {zoneDetail?.children && zoneDetail.children.length > 0 && (
                                <div className="chart-card" style={{ marginBottom: '16px' }}>
                                    <h4 style={{ margin: '0 0 8px', fontSize: '0.9rem' }}><FiGitBranch /> Sous-zones ({zoneDetail.children.length})</h4>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                        {zoneDetail.children.map((child: any) => (
                                            <div
                                                key={child.id}
                                                style={{
                                                    display: 'flex', justifyContent: 'space-between',
                                                    alignItems: 'center', padding: '6px 10px',
                                                    borderRadius: '6px', backgroundColor: '#F9FAFB',
                                                    cursor: 'pointer', fontSize: '0.85rem'
                                                }}
                                                onClick={() => {
                                                    const z = flatZones.find(fz => fz.id === child.id);
                                                    if (z) handleSelectZone(z);
                                                }}
                                            >
                                                <span>{child.nom}</span>
                                                <TypeBadge type={child.type} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Map preview */}
                            {selectedZone.latitude_centre && selectedZone.longitude_centre && (
                                <div className="chart-card">
                                    <h4 style={{ margin: '0 0 8px', fontSize: '0.9rem' }}><FiMapPin /> Localisation</h4>
                                    <div style={{
                                        width: '100%', height: '200px', borderRadius: '8px',
                                        overflow: 'hidden', border: '1px solid #E5E7EB'
                                    }}>
                                        <iframe
                                            title="Zone map"
                                            width="100%"
                                            height="100%"
                                            frameBorder="0"
                                            style={{ border: 0 }}
                                            src={`https://www.openstreetmap.org/export/embed.html?bbox=${
                                                (selectedZone.longitude_centre! - 0.05).toFixed(4)
                                            },${
                                                (selectedZone.latitude_centre! - 0.05).toFixed(4)
                                            },${
                                                (selectedZone.longitude_centre! + 0.05).toFixed(4)
                                            },${
                                                (selectedZone.latitude_centre! + 0.05).toFixed(4)
                                            }&layer=mapnik&marker=${selectedZone.latitude_centre},${selectedZone.longitude_centre}`}
                                        />
                                    </div>
                                    <div className="text-muted" style={{ fontSize: '0.8rem', marginTop: 6 }}>
                                        {selectedZone.latitude_centre?.toFixed(5)}, {selectedZone.longitude_centre?.toFixed(5)}
                                        {selectedZone.rayon_km && ` | Rayon: ${selectedZone.rayon_km} km`}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Liste Tab */}
            {tab === 'liste' && (
                <>
                    <div className="filters-bar">
                        <div className="search-box">
                            <FiSearch />
                            <input
                                placeholder="Rechercher..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </div>
                        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                            <option value="">Tous les types</option>
                            {ZONE_TYPES.map(t => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                        </select>
                        <select value={parentFilter} onChange={e => setParentFilter(e.target.value)}>
                            <option value="">Tous les parents</option>
                            {flatZones.filter(z => z.type !== 'point').map(z => (
                                <option key={z.id} value={z.id}>{z.nom} ({z.type})</option>
                            ))}
                        </select>
                    </div>

                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th onClick={() => handleSort('nom')} style={{ cursor: 'pointer' }}>
                                        Nom{sortIcon('nom')}
                                    </th>
                                    <th onClick={() => handleSort('code')} style={{ cursor: 'pointer' }}>
                                        Code{sortIcon('code')}
                                    </th>
                                    <th onClick={() => handleSort('type')} style={{ cursor: 'pointer' }}>
                                        Type{sortIcon('type')}
                                    </th>
                                    <th>Parent</th>
                                    <th>Responsable</th>
                                    <th onClick={() => handleSort('nb_commerciaux')} style={{ cursor: 'pointer' }}>
                                        Commerciaux{sortIcon('nb_commerciaux')}
                                    </th>
                                    <th onClick={() => handleSort('nb_clients')} style={{ cursor: 'pointer' }}>
                                        Clients{sortIcon('nb_clients')}
                                    </th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredFlatZones.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} style={{ textAlign: 'center', padding: '24px', color: '#6B7280' }}>
                                            Aucune zone trouvee
                                        </td>
                                    </tr>
                                ) : (
                                    filteredFlatZones.map(z => (
                                        <tr key={z.id}>
                                            <td><strong>{z.nom}</strong></td>
                                            <td><code style={{ fontSize: '0.8rem', color: '#6B7280' }}>{z.code}</code></td>
                                            <td><TypeBadge type={z.type} /></td>
                                            <td>{z.parent_nom || '-'}</td>
                                            <td>
                                                {z.responsable_nom
                                                    ? `${z.responsable_prenom || ''} ${z.responsable_nom}`
                                                    : <span className="text-muted">-</span>
                                                }
                                            </td>
                                            <td>{z.nb_commerciaux ?? 0}</td>
                                            <td>{z.nb_clients ?? 0}</td>
                                            <td className="actions">
                                                <button className="btn-icon" onClick={() => openEditForm(z)} title="Modifier">
                                                    <FiEdit2 />
                                                </button>
                                                <button className="btn-icon" onClick={() => openAssignModal(z)} title="Assigner responsable">
                                                    <FiUserPlus />
                                                </button>
                                                <button className="btn-icon btn-icon-danger" onClick={() => openDeleteConfirm(z)} title="Supprimer">
                                                    <FiTrash2 />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {/* Create/Edit Modal */}
            {showFormModal && (
                <div className="modal-overlay" onClick={() => setShowFormModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                        <div className="modal-header">
                            <h3>{editZone ? 'Modifier la zone' : 'Nouvelle zone'}</h3>
                            <button className="btn-icon" onClick={() => setShowFormModal(false)}><FiX /></button>
                        </div>
                        <form onSubmit={handleFormSubmit}>
                            <div className="modal-body">
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>Nom *</label>
                                        <input
                                            type="text"
                                            value={form.nom}
                                            onChange={e => setForm({ ...form, nom: e.target.value })}
                                            placeholder="Nom de la zone"
                                            className={formErrors.nom ? 'input-error' : ''}
                                        />
                                        {formErrors.nom && <span className="error-text">{formErrors.nom}</span>}
                                    </div>
                                    <div className="form-group">
                                        <label>Code *</label>
                                        <input
                                            type="text"
                                            value={form.code}
                                            onChange={e => setForm({ ...form, code: e.target.value })}
                                            placeholder="Code unique"
                                            className={formErrors.code ? 'input-error' : ''}
                                        />
                                        {formErrors.code && <span className="error-text">{formErrors.code}</span>}
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Description</label>
                                    <textarea
                                        value={form.description}
                                        onChange={e => setForm({ ...form, description: e.target.value })}
                                        placeholder="Description de la zone"
                                        rows={2}
                                    />
                                </div>

                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>Parent</label>
                                        <select
                                            value={form.parent_id}
                                            onChange={e => handleParentChange(e.target.value)}
                                        >
                                            <option value="">Aucun (zone racine)</option>
                                            {parentOptions
                                                .filter(p => !editZone || p.id !== editZone.id)
                                                .map(p => (
                                                    <option key={p.id} value={p.id}>{p.label}</option>
                                                ))
                                            }
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Type *</label>
                                        <select
                                            value={form.type}
                                            onChange={e => setForm({ ...form, type: e.target.value })}
                                            className={formErrors.type ? 'input-error' : ''}
                                        >
                                            {ZONE_TYPES.map(t => (
                                                <option key={t.value} value={t.value}>{t.label}</option>
                                            ))}
                                        </select>
                                        {formErrors.type && <span className="error-text">{formErrors.type}</span>}
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Responsable</label>
                                    <select
                                        value={form.responsable_id}
                                        onChange={e => setForm({ ...form, responsable_id: e.target.value })}
                                    >
                                        <option value="">Aucun responsable</option>
                                        {managersAndAdmins.map(u => (
                                            <option key={u.id} value={u.id}>
                                                {u.prenom} {u.nom} ({u.role})
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-grid form-grid-3">
                                    <div className="form-group">
                                        <label>Latitude centre</label>
                                        <input
                                            type="number"
                                            step="any"
                                            value={form.latitude_centre}
                                            onChange={e => setForm({ ...form, latitude_centre: e.target.value })}
                                            placeholder="14.6928"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Longitude centre</label>
                                        <input
                                            type="number"
                                            step="any"
                                            value={form.longitude_centre}
                                            onChange={e => setForm({ ...form, longitude_centre: e.target.value })}
                                            placeholder="-17.4467"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Rayon (km)</label>
                                        <input
                                            type="number"
                                            step="0.1"
                                            min="0"
                                            value={form.rayon_km}
                                            onChange={e => setForm({ ...form, rayon_km: e.target.value })}
                                            placeholder="10"
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Couleur</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <input
                                            type="color"
                                            value={form.couleur}
                                            onChange={e => setForm({ ...form, couleur: e.target.value })}
                                            style={{ width: '48px', height: '36px', border: '1px solid #D1D5DB', borderRadius: '6px', padding: '2px', cursor: 'pointer' }}
                                        />
                                        <input
                                            type="text"
                                            value={form.couleur}
                                            onChange={e => setForm({ ...form, couleur: e.target.value })}
                                            placeholder="#3B82F6"
                                            style={{ flex: 1 }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowFormModal(false)}>
                                    Annuler
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={submitting}>
                                    {submitting ? 'Enregistrement...' : (editZone ? 'Modifier' : 'Creer')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Assign Responsable Modal */}
            {showAssignModal && assignTarget && (
                <div className="modal-overlay" onClick={() => setShowAssignModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '450px' }}>
                        <div className="modal-header">
                            <h3>Assigner un responsable</h3>
                            <button className="btn-icon" onClick={() => setShowAssignModal(false)}><FiX /></button>
                        </div>
                        <form onSubmit={handleAssignSubmit}>
                            <div className="modal-body">
                                <p style={{ marginBottom: '12px', color: '#374151' }}>
                                    Zone: <strong>{assignTarget.nom}</strong> <TypeBadge type={assignTarget.type} />
                                </p>
                                {assignTarget.responsable_nom && (
                                    <p className="text-muted" style={{ marginBottom: '12px', fontSize: '0.85rem' }}>
                                        Responsable actuel: {assignTarget.responsable_prenom} {assignTarget.responsable_nom}
                                    </p>
                                )}
                                <div className="form-group">
                                    <label>Nouveau responsable</label>
                                    <select
                                        value={assignResponsableId}
                                        onChange={e => setAssignResponsableId(e.target.value)}
                                    >
                                        <option value="">Aucun responsable</option>
                                        {managersAndAdmins.map(u => (
                                            <option key={u.id} value={u.id}>
                                                {u.prenom} {u.nom} ({u.role})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowAssignModal(false)}>
                                    Annuler
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={submitting}>
                                    {submitting ? 'Enregistrement...' : 'Assigner'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && deleteTarget && (
                <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px' }}>
                        <div className="modal-header">
                            <h3>Confirmer la suppression</h3>
                            <button className="btn-icon" onClick={() => setShowDeleteConfirm(false)}><FiX /></button>
                        </div>
                        <div className="modal-body">
                            <p style={{ color: '#374151' }}>
                                Voulez-vous vraiment supprimer la zone <strong>{deleteTarget.nom}</strong> ?
                            </p>
                            {deleteTarget.children && deleteTarget.children.length > 0 && (
                                <p style={{ color: '#DC2626', fontSize: '0.85rem', marginTop: '8px' }}>
                                    Attention: cette zone contient des sous-zones qui seront egalement affectees.
                                </p>
                            )}
                            <p className="text-muted" style={{ fontSize: '0.85rem', marginTop: '8px' }}>
                                Cette action est irreversible.
                            </p>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={() => setShowDeleteConfirm(false)}>
                                Annuler
                            </button>
                            <button className="btn btn-danger" onClick={handleDelete} disabled={submitting}>
                                {submitting ? 'Suppression...' : 'Supprimer'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Scoped Styles */}
            <style>{`
                .page-zones {
                    padding: 0;
                }

                .zone-arborescence-layout {
                    display: flex;
                    gap: 20px;
                    align-items: flex-start;
                }

                .zone-tree-panel {
                    flex: 1;
                    min-width: 0;
                }

                .zone-detail-panel {
                    width: 360px;
                    flex-shrink: 0;
                    background: #fff;
                    border-radius: 12px;
                    border: 1px solid #E5E7EB;
                    padding: 20px;
                    position: sticky;
                    top: 20px;
                    max-height: calc(100vh - 200px);
                    overflow-y: auto;
                }

                .zone-detail-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 12px;
                }

                .zone-detail-header h3 {
                    margin: 0 0 6px;
                    font-size: 1.1rem;
                }

                .tree-controls {
                    display: flex;
                    gap: 6px;
                }

                .btn-sm {
                    padding: 4px 10px !important;
                    font-size: 0.8rem !important;
                }

                .tree-container {
                    background: #fff;
                    border-radius: 12px;
                    border: 1px solid #E5E7EB;
                    overflow: hidden;
                }

                .tree-node {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    padding: 10px 12px;
                    border-bottom: 1px solid #F3F4F6;
                    cursor: pointer;
                    transition: background-color 0.15s;
                    position: relative;
                }

                .tree-node:hover {
                    background-color: #F9FAFB;
                }

                .tree-node-selected {
                    background-color: #EFF6FF !important;
                    border-left: 3px solid #3B82F6;
                }

                .tree-line {
                    position: absolute;
                    top: 0;
                    bottom: 0;
                    width: 0;
                }

                .tree-toggle {
                    background: none;
                    border: none;
                    cursor: pointer;
                    padding: 2px;
                    color: #6B7280;
                    display: flex;
                    align-items: center;
                    flex-shrink: 0;
                }

                .tree-toggle:hover {
                    color: #111827;
                }

                .tree-node-content {
                    flex: 1;
                    min-width: 0;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                }

                .tree-node-main {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    min-width: 0;
                }

                .tree-node-name {
                    font-weight: 500;
                    color: #111827;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    font-size: 0.9rem;
                }

                .tree-node-meta {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    flex-shrink: 0;
                }

                .tree-meta-item {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    font-size: 0.78rem;
                    color: #6B7280;
                    white-space: nowrap;
                }

                .tree-node-actions {
                    display: flex;
                    gap: 2px;
                    opacity: 0;
                    transition: opacity 0.15s;
                    flex-shrink: 0;
                }

                .tree-node:hover .tree-node-actions {
                    opacity: 1;
                }

                .btn-icon-danger {
                    color: #DC2626 !important;
                }

                .btn-icon-danger:hover {
                    background-color: #FEF2F2 !important;
                }

                .form-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 12px;
                }

                .form-grid-3 {
                    grid-template-columns: 1fr 1fr 1fr;
                }

                .form-group {
                    margin-bottom: 14px;
                }

                .form-group label {
                    display: block;
                    font-size: 0.85rem;
                    font-weight: 500;
                    color: #374151;
                    margin-bottom: 4px;
                }

                .form-group input,
                .form-group select,
                .form-group textarea {
                    width: 100%;
                    padding: 8px 12px;
                    border: 1px solid #D1D5DB;
                    border-radius: 8px;
                    font-size: 0.9rem;
                    transition: border-color 0.15s;
                    background: #fff;
                    box-sizing: border-box;
                }

                .form-group input:focus,
                .form-group select:focus,
                .form-group textarea:focus {
                    outline: none;
                    border-color: #3B82F6;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                }

                .form-group textarea {
                    resize: vertical;
                }

                .input-error {
                    border-color: #DC2626 !important;
                }

                .error-text {
                    color: #DC2626;
                    font-size: 0.78rem;
                    margin-top: 2px;
                    display: block;
                }

                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                @media (max-width: 1024px) {
                    .zone-arborescence-layout {
                        flex-direction: column;
                    }
                    .zone-detail-panel {
                        width: 100%;
                        position: static;
                        max-height: none;
                    }
                }

                @media (max-width: 768px) {
                    .tree-node-meta {
                        display: none;
                    }
                    .tree-node-actions {
                        opacity: 1;
                    }
                    .form-grid,
                    .form-grid-3 {
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>
        </div>
    );
};

export default ZoneManagement;
